import React, { useState } from 'react';
import { BarChart3, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, Search } from 'lucide-react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { obrasApi } from '@/core/adapter-api';
import { StatusBadge, StatsGrid, EmptyState } from './shared';

// ─── MiniGantt ────────────────────────────────────────────────────────────────

const MiniGantt: React.FC<{ tasks: any[] }> = ({ tasks }) => {
  if (tasks.length === 0) return null;

  let minDate = new Date();
  let maxDate = new Date();
  minDate.setDate(minDate.getDate() - 7);
  maxDate.setDate(maxDate.getDate() + 21);

  const validTasks = tasks.filter(t => t.startDate && t.endDate);
  if (validTasks.length > 0) {
    const starts = validTasks.map(t => new Date(t.startDate).getTime());
    const ends = validTasks.map(t => new Date(t.endDate).getTime());
    minDate = new Date(Math.min(...starts) - 86400000 * 2);
    maxDate = new Date(Math.max(...ends) + 86400000 * 5);
  }

  const totalDays = Math.max(1, Math.round((maxDate.getTime() - minDate.getTime()) / 86400000));

  return (
    <div className="mt-4 border border-white/10 rounded-xl bg-[#010409] p-4 overflow-x-auto">
      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">Cronograma (Gantt)</h4>
      <div className="min-w-[500px] relative">
        <div className="flex border-b border-white/5 pb-2 mb-3 relative h-6">
          {Array.from({ length: totalDays }).map((_, i) => {
            const d = new Date(minDate.getTime() + i * 86400000);
            return (
              <div key={i} className="flex-1 text-center relative" style={{ minWidth: '30px' }}>
                {d.getDate() === 1 || i === 0 || d.getDay() === 1 ? (
                  <span className="text-[9px] text-gray-500 absolute -bottom-1 left-0">{d.getDate()}/{d.getMonth()+1}</span>
                ) : null}
                <div className="absolute top-6 w-px h-[200px] bg-white/5 left-1/2" />
              </div>
            );
          })}
        </div>

        <div className="space-y-3 relative z-10 pb-4">
          {tasks.map(task => {
            const start = task.startDate ? new Date(task.startDate) : new Date();
            const end = task.endDate ? new Date(task.endDate) : new Date(start.getTime() + 86400000 * 3);
            const startOffset = Math.max(0, Math.round((start.getTime() - minDate.getTime()) / 86400000));
            const duration = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
            const leftPct = (startOffset / totalDays) * 100;
            const widthPct = (duration / totalDays) * 100;
            const colorClass = task.status === 'done' ? 'bg-emerald-500' : task.status === 'in_progress' ? 'bg-amber-500' : 'bg-gray-500';

            return (
              <div key={task.id} className="relative h-6 rounded flex items-center group">
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 rounded transition-opacity" />
                <div
                  className={`absolute h-5 rounded hover:brightness-110 transition-all cursor-pointer shadow-sm ${colorClass}`}
                  style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                  title={`${task.name} (${start.toLocaleDateString()} - ${end.toLocaleDateString()})`}
                >
                  <span className="absolute inset-y-0 left-2 flex items-center text-[10px] font-bold text-white/90 truncate max-w-full px-1">
                    {task.name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

interface SeguimientoItem {
  id: string;
  name: string;
  detail: string;
  status: string;
  progress: number;
  budget: number;
  spent: number;
  issues: string[];
  startDate: string;
  endDate: string;
}

export const SeguimientoPage: React.FC = () => {
  const rawObras = useQuery(() => obrasApi.all(), [], ['obras']) || [];
  
  const allTasksQuery = useQuery(async () => {
    const { getAdapter } = await import('@/core/db-adapter');
    return getAdapter().query<any>('obra_tasks');
  }, [], ['obra_tasks']) || [];

  const items: SeguimientoItem[] = rawObras.map(obra => {
    const pTasks = allTasksQuery.filter(t => t.obraId === obra.id);
    const doneTasks = pTasks.filter(t => t.status === 'done').length;
    const progress = pTasks.length > 0 ? Math.round((doneTasks / pTasks.length) * 100) : 0;
    const spent = Math.round(obra.budget * progress / 100);
    // Compute end date from tasks or default to +90 days from start
    const taskEnds = pTasks.filter(t => t.endDate).map(t => new Date(t.endDate).getTime());
    const estimatedEnd = taskEnds.length > 0
      ? new Date(Math.max(...taskEnds)).toISOString().split('T')[0]
      : obra.startDate ? new Date(new Date(obra.startDate).getTime() + 90 * 86400000).toISOString().split('T')[0] : '';
    
    return {
      id: obra.id,
      name: obra.name,
      detail: obra.location || 'Sin ubicación',
      status: obra.status === 'terminada' ? 'Completado' : obra.status === 'en_obra' ? 'Activo' : 'Pendiente',
      progress,
      budget: obra.budget,
      spent,
      issues: pTasks.filter(t => t.status !== 'done').slice(0, 3).map(t => 'Tarea pdte: ' + t.name),
      startDate: obra.startDate || '',
      endDate: estimatedEnd,
    };
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const toggleStatus = async (id: string) => {
    const obra = rawObras.find(o => o.id === id);
    if (!obra) return;
    const nextStatus = obra.status === 'en_obra' ? 'terminada' : obra.status === 'presupuestando' ? 'en_obra' : 'presupuestando';
    await obrasApi.update(id, { status: nextStatus as any });
  };

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const avgProgress = items.length > 0 ? Math.round(items.reduce((s, i) => s + i.progress, 0) / items.length) : 0;
  const delayed = items.filter(i => i.status === 'Pendiente').length;
  const openIssues = items.reduce((s, i) => s + i.issues.length, 0);
  const pendingCerts = items.filter(i => i.detail.includes('Certificación')).length;
  const filteredItems = searchQuery
    ? items.filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()) || i.detail.toLowerCase().includes(searchQuery.toLowerCase()))
    : items;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/15 rounded-xl">
            <BarChart3 size={20} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Seguimiento de Obras</h1>
            <p className="text-sm text-gray-500">Control de avance y desviaciones</p>
          </div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar obra..."
            className="pl-9 pr-3 py-2 bg-[#0d1117] border border-white/10 rounded-xl text-sm text-gray-200 placeholder:text-gray-600 focus:border-amber-500/50 focus:outline-none w-48"
          />
        </div>
      </div>

      <StatsGrid stats={[
        { label: 'Avance medio', value: `${avgProgress}%`, color: 'text-emerald-400' },
        { label: 'Obras retrasadas', value: String(delayed), color: 'text-amber-400' },
        { label: 'Incidencias abiertas', value: String(openIssues), color: 'text-red-400' },
        { label: 'Certificaciones pend.', value: String(pendingCerts) },
      ]} />

      <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/8">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Registros</h3>
        </div>
        <div className="divide-y divide-white/5">
          {filteredItems.map((item) => (
            <div key={item.id}>
              <div
                className={`flex items-center justify-between px-5 py-3 cursor-pointer transition-colors ${
                  selectedId === item.id ? 'bg-amber-500/5' : 'hover:bg-white/3'
                }`}
                onClick={() => { setSelectedId(selectedId === item.id ? null : item.id); toggleExpand(item.id); }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <button className="text-gray-500">
                    {expanded.has(item.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-200 font-medium truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">{item.detail}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="hidden sm:block w-24">
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${item.progress >= 100 ? 'bg-emerald-500' : item.progress >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-gray-500 mt-0.5 text-right">{item.progress}%</p>
                  </div>
                  <StatusBadge status={item.status} onClick={() => toggleStatus(item.id)} />
                </div>
              </div>

              {expanded.has(item.id) && (
                <div className="px-5 pb-4 bg-[#0a0e14] border-t border-white/5">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 py-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Presupuesto</p>
                      <p className="text-lg font-bold text-gray-100">{(item.budget / 1000).toFixed(0)}k€</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Gastado</p>
                      <p className="text-lg font-bold text-amber-400">{(item.spent / 1000).toFixed(0)}k€</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Desviación</p>
                      <p className={`text-lg font-bold ${item.spent <= item.budget ? 'text-emerald-400' : 'text-red-400'}`}>
                        {((item.spent / item.budget - 1) * 100).toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Inicio</p>
                      <p className="text-sm font-bold text-gray-300">{item.startDate ? new Date(item.startDate + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Fin estimado</p>
                      <p className="text-sm font-bold text-gray-300">{item.endDate ? new Date(item.endDate + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}</p>
                      {item.endDate && (() => {
                        const days = Math.ceil((new Date(item.endDate + 'T00:00:00').getTime() - Date.now()) / 86400000);
                        return days > 0 ? <p className="text-[10px] text-amber-400 font-bold mt-0.5">{days} días</p> : <p className="text-[10px] text-red-400 font-bold mt-0.5">Vencido</p>;
                      })()}
                    </div>
                  </div>

                  <MiniGantt tasks={allTasksQuery.filter(t => t.obraId === item.id)} />

                  {item.issues.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Incidencias</p>
                      <ul className="space-y-1.5">
                        {item.issues.map((issue, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                            <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
                            {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {item.issues.length === 0 && (
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <CheckCircle2 size={14} />
                      Sin incidencias abiertas
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {filteredItems.length === 0 && (
            <EmptyState
              icon={BarChart3}
              title="Sin obras registradas"
              description="Crea tu primera obra en la sección de Obras para comenzar a hacer seguimiento."
            />
          )}
        </div>
      </div>
    </div>
  );
};
